import type { CheckboxFieldProps } from '@iheartjane/reefer';
import { CheckboxField } from '@iheartjane/reefer';

import type { FormFieldProps } from '../formField.types';
import { FormFieldWrapper } from '../formFieldWrapper/formFieldWrapper';

export interface FormCheckboxFieldProps
  extends
    Omit<CheckboxFieldProps, 'children'>,
    Pick<FormFieldProps, 'required' | 'shouldUnregister' | 'validate'> {}

/**
 * Checkbox fields allow users to choose an option, or not.
 *
 * Use this component *within forms* for inputs of `type`:
 * `checkbox`
 *
 * _Field validations supported:_ `required`, `validate`.
 *
 * [This example](/story/reefer-hook-form-examples-form-checkboxfield-group--default) illustrates
 * how a group of child `Form.CheckboxField` inputs can be controlled by a parent `Form.CheckboxField` input.
 *
 * **NOTE: DO NOT USE THIS COMPONENT OUTSIDE FORMS**
 *
 * _For a similar component for use outside forms, see [`CheckboxField`](/story/components-forms-fields-checkboxfield--default)._
 */
export function FormCheckboxField({
  autoFocus = false,
  checked,
  defaultChecked = false,
  disabled = false,
  helperText,
  indeterminate,
  label,
  labelHidden = false,
  name,
  onBlur,
  onChange,
  onChangeIndeterminate,
  onClick,
  required = false,
  'data-testid': testId,
  shouldUnregister,
  validate,
  width = 'auto',
  ...props
}: FormCheckboxFieldProps) {
  return (
    <FormFieldWrapper
      defaultValue={defaultChecked}
      name={name}
      render={({ children, field, invalid }) => (
        <CheckboxField
          aria-invalid={invalid}
          autoFocus={autoFocus}
          checked={checked}
          defaultChecked={defaultChecked}
          disabled={disabled}
          helperText={helperText}
          indeterminate={indeterminate}
          label={label}
          labelHidden={labelHidden}
          name={field.name}
          onBlur={(value) => {
            onBlur && onBlur(value);
            field.onBlur();
          }}
          onChange={(value) => {
            onChange && onChange(value);
            field.onChange(value);
          }}
          onChangeIndeterminate={(value) => {
            onChangeIndeterminate && onChangeIndeterminate(value);
            field.onChange(value ? 'indeterminate' : false);
          }}
          onClick={onClick}
          onUpdate={onChange}
          required={required}
          ref={field.ref}
          data-testid={testId}
          width={width}
          externalChecked={field.value}
          {...props}
        >
          {children}
        </CheckboxField>
      )}
      required={required}
      shouldUnregister={shouldUnregister}
      validate={validate}
    />
  );
}
